import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, InputBase, Paper } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
// Local
import { useSelector } from "../../state";
import { Navigation } from "../../lib";

const { REACT_APP_API_URL } = process.env;

// Rows-per-page options for the pager dropdown. The chosen value flows to the
// data request (?Num) and to TreeGrid's PageLength, so this is the single
// source of truth for page size.
const PAGE_SIZES = [50, 100, 200, 400, 500];
const DEFAULT_PAGE_SIZE = 200;

// Search / sort / paging state, kept in React state and mirrored to the URL
// via history.pushState. Navigation is IN-PLACE: the grid re-fetches its data
// through TreeGrid's own ReloadBody() API — no full page reload, nothing else
// on the page moves. (The route key for /watches-list is pathname-only, so
// query-string history changes never remount this component.)
function readUrlQuery() {
  const p = new URLSearchParams(window.location.search);
  const size = PAGE_SIZES.includes(parseInt(p.get("size"), 10))
    ? parseInt(p.get("size"), 10)
    : DEFAULT_PAGE_SIZE;
  return {
    search: p.get("search") || "",
    sort: p.get("sort") || "",
    dir: p.get("dir") === "desc" ? "desc" : "asc",
    page: Math.max(0, parseInt(p.get("page"), 10) || 0),
    size,
  };
}

// The CURRENT live grid instance. Verified: TGGrids ACCUMULATES — after an SPA
// re-mount or a rebuild the old instance stays at [0] and the live one is
// appended — so scan from the END (newest build wins). __tgGrid (set by the
// layout's OnReady) is checked first but validated: it may never be set.
function findLiveGrid() {
  const usable = (g) =>
    g && g.Source && g.Source.Data && typeof g.ReloadBody === "function" ? g : null;
  let grid = usable(window.__tgGrid);
  if (!grid && window.TGGrids) {
    for (let i = window.TGGrids.length - 1; i >= 0 && !grid; i--) {
      grid = usable(window.TGGrids[i]);
    }
  }
  return grid;
}

function buildPageQs(q) {
  const params = new URLSearchParams();
  if (q.search) params.set("search", q.search);
  if (q.sort) {
    params.set("sort", q.sort);
    params.set("dir", q.dir);
  }
  if (q.size !== DEFAULT_PAGE_SIZE) params.set("size", String(q.size));
  if (q.page > 0) params.set("page", String(q.page));
  return params.toString();
}

function _WatchesPage() {
  const token = useSelector((state) => state.auth.token);
  // Site scope from the sidebar selector. -1 = not yet resolved (fresh load
  // before getDefaultScope runs); fall back to PT (0) so the grid never bakes
  // site=-1 into its data URL. A scope change reloads the page, and the store
  // preloads `scopes` from localStorage synchronously, so on reload this is
  // already the selected site at first render.
  const siteId = useSelector((state) => state.scopes.site_id);
  const effectiveSite = siteId >= 0 ? siteId : 0;

  const [query, setQuery] = useState(readUrlQuery);
  const [searchInput, setSearchInput] = useState(query.search);
  // 'checking' while the count pre-fetch runs; then 'ready' / 'empty' / 'failed'.
  const [gridStatus, setGridStatus] = useState("checking");
  // Total matching rows (from the count pre-check) — drives the pager.
  const [total, setTotal] = useState(null);
  // Tracks whether TreeGrid has actually built its DOM (it REPLACES the
  // <treegrid> tag with its own markup when it does).
  const [gridBuilt, setGridBuilt] = useState(false);
  const gridBuiltRef = useRef(false);
  gridBuiltRef.current = gridBuilt;
  const gridWrapRef = useRef(null);

  // Latest-value refs for the stable window.* handlers and async callbacks.
  const queryRef = useRef(query);
  queryRef.current = query;
  const statusRef = useRef(gridStatus);
  statusRef.current = gridStatus;
  // Column currently showing a ▲/▼ header arrow (the initial one is baked into
  // the layout by the backend; later changes are applied via the grid API).
  const prevSortRef = useRef(query.sort || null);

  const buildDataUrl = (q) => {
    const params = new URLSearchParams();
    params.set("site", String(effectiveSite));
    if (q.search) params.set("search", q.search);
    if (q.sort) {
      params.set("sort", q.sort);
      params.set("dir", q.dir);
    }
    if (q.page > 0) params.set("pageStart", String(q.page * q.size));
    // Explicitly set the slice size (SQL LIMIT). TreeGrid also appends Num from
    // PageLength; if both are present they carry the same value, so the
    // backend's parseInt resolves it either way.
    if (q.size !== DEFAULT_PAGE_SIZE) params.set("Num", String(q.size));
    // Image host for the optional thumbnail column, so the backend can build
    // the <img src> (matches Images.jsx IMAGES_BASE_URL). Harmless when the
    // column is hidden — the backend still returns the markup, just unused.
    if (process.env.REACT_APP_BASEURL) params.set("imgBase", process.env.REACT_APP_BASEURL);
    return `${REACT_APP_API_URL}/watches-grid?${params.toString()}`;
  };
  const dataUrl = buildDataUrl(query);
  const dataUrlRef = useRef(dataUrl);
  dataUrlRef.current = dataUrl;

  const buildLayoutUrl = (q) => {
    const layoutParams = new URLSearchParams();
    layoutParams.set("site", String(effectiveSite));
    if (q.sort) {
      layoutParams.set("sort", q.sort);
      layoutParams.set("dir", q.dir);
    }
    if (q.size !== DEFAULT_PAGE_SIZE) layoutParams.set("size", String(q.size));
    return `${REACT_APP_API_URL}/grid-layout?${layoutParams.toString()}`;
  };

  // The <treegrid> tag's URLs are FROZEN per grid build: TreeGrid reads them
  // once while building and then REMOVES the tag from the DOM (verified — it
  // renders its own DIV), so React must never churn these attributes. Normal
  // navigation goes through reloadGrid(); a rebuild (see rebuildGrid) refreshes
  // them to the current query first. The layout_url carries the initial sort
  // (header ▲/▼) and page size (PageLength).
  const initialTagRef = useRef(null);
  if (initialTagRef.current === null) {
    initialTagRef.current = { dataUrl, layoutUrl: buildLayoutUrl(query) };
  }

  // Incremented to tear down and rebuild the grid from scratch (fresh wrap +
  // fresh <treegrid> tag + StartTreeGrid rescan) — the recovery path when the
  // engine wedges into its "Cannot download this data part!" state. The KEY
  // sits on the WRAP div, not the tag: TreeGrid removes the tag from the DOM
  // while building, so React re-keying the tag itself would crash unmounting
  // a node that's no longer there; the wrap is React-owned and safe.
  const [gridEpoch, setGridEpoch] = useState(0);
  // One automatic rebuild per data URL — prevents a rebuild loop if the
  // backend is genuinely failing.
  const rebuiltForUrlRef = useRef(null);
  const rebuildGrid = () => {
    initialTagRef.current = {
      dataUrl: dataUrlRef.current,
      layoutUrl: buildLayoutUrl(queryRef.current),
    };
    // The fresh layout bakes the current sort arrow in; reset the tracker.
    prevSortRef.current = queryRef.current.sort || null;
    reloadingRef.current = false;
    // NOTE: TreeGrid's debug strip (GridDebug* elements on BODY) must NOT be
    // removed from the DOM — the engine keeps a live reference and writes to
    // it from global handlers (even for old grid instances reacting to window
    // resize), so deleting it turns a benign log into a `null.style` crash.
    // It's hidden via CSS in the page style block instead.
    setGridBuilt(false);
    setGridEpoch((e) => e + 1);
  };

  // TreeGrid renders download failures as text inside the grid area.
  const gridHasError = () => {
    const wrap = gridWrapRef.current;
    return !!wrap && /Cannot download|Failed downloading/i.test(wrap.innerText || "");
  };

  // The grid re-lays-out (re-reading row heights and container size) on a
  // window resize — the one public signal the compressed engine responds to.
  const nudge = () => {
    try {
      window.dispatchEvent(new Event("resize"));
    } catch (e) {
      /* noop */
    }
  };

  // Move the header sort arrow without reloading the layout: strip the ▲/▼
  // off the previously sorted column's caption and append it to the new one.
  // Purely cosmetic — guarded so an engine-API mismatch can never break
  // navigation itself.
  const applySortArrows = (grid) => {
    try {
      const H = grid.Header;
      if (!H || typeof grid.RefreshRow !== "function") return;
      const strip = (v) => String(v == null ? "" : v).replace(/ [▲▼]$/, "");
      const q = queryRef.current;
      const prev = prevSortRef.current;
      let changed = false;
      if (prev && prev !== q.sort && H[prev] != null) {
        H[prev] = strip(H[prev]);
        changed = true;
      }
      if (q.sort && H[q.sort] != null) {
        const want = strip(H[q.sort]) + (q.dir === "desc" ? " ▼" : " ▲");
        if (H[q.sort] !== want) {
          H[q.sort] = want;
          changed = true;
        }
      }
      prevSortRef.current = q.sort || null;
      if (changed) grid.RefreshRow(H);
    } catch (e) {
      /* cosmetic only */
    }
  };

  // In-place refresh: point TreeGrid's data source at the new URL and let its
  // own ReloadBody() re-fetch just the body rows (vendor-documented API:
  // grid.Source.Data.Url = url; grid.ReloadBody()). The page, sidebar and grid
  // chrome (column widths, chooser selections, scroll canvas) stay put — this
  // is how the grid behaved in the old admin. No-ops until the grid exists;
  // the build-completion path re-syncs a URL that changed mid-build.
  //
  // ⚠️ Reloads are SERIALIZED. ReloadBody is async; firing a second one while
  // the first is still downloading corrupts the engine's paged-download state
  // (verified: the grid either sticks on the stale dataset or throws its
  // "Cannot download this data part!" page error — hit in practice by
  // backspacing a search empty, where the clear fires while the intermediate
  // search's slower query is still in flight). One reload runs at a time; if
  // the wanted URL changes meanwhile, the completion callback chains the next
  // reload. Sort arrows / layout nudges also wait for completion — touching
  // the grid mid-download is what breaks it.
  const reloadingRef = useRef(false);
  const reloadGrid = () => {
    // Only touch the grid once THIS mount's build has been detected — before
    // that, window.__tgGrid / TGGrids[0] may still reference a torn-down
    // instance from a previous mount (SPA back/forward). A URL that changes
    // mid-build is re-synced by the query effect when gridBuilt flips true.
    if (!gridBuiltRef.current) return;
    const grid = findLiveGrid();
    if (!grid) return;
    if (reloadingRef.current) return; // busy — completion callback re-checks
    const url = dataUrlRef.current;
    const q = queryRef.current;
    if (grid.Source.Data.Url === url) {
      applySortArrows(grid);
      return;
    }
    grid.Source.Data.Url = url;
    if (+grid.PageLength !== q.size) {
      try {
        grid.PageLength = q.size;
      } catch (e) {
        /* page-size attr is advisory; Num on the URL still bounds the query */
      }
    }
    reloadingRef.current = true;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      reloadingRef.current = false;
      // Wanted URL moved on while this download ran? Chain the next reload
      // FIRST — before any nudge/arrow work. A resize dispatched here wakes
      // the engine's page-update machinery, and starting the next ReloadBody
      // on top of that is a mid-swap collision; superseded loads don't need
      // cosmetics anyway. Deferred a beat so the engine fully settles the
      // completed swap before the next one starts.
      if (dataUrlRef.current !== grid.Source.Data.Url) {
        setTimeout(reloadGrid, 80);
        return;
      }
      applySortArrows(grid);
      nudge();
      // Let the swap settle, then verify the engine didn't wedge into its
      // "Cannot download this data part!" state — if it did, tear down and
      // rebuild the grid fresh at the current URL (grid area only; sidebar and
      // page stay put). Guarantees a clear-search always lands on a clean
      // default grid. Once per URL so a genuinely failing backend can't loop.
      setTimeout(() => {
        if (
          dataUrlRef.current === grid.Source.Data.Url &&
          gridHasError() &&
          rebuiltForUrlRef.current !== dataUrlRef.current
        ) {
          rebuiltForUrlRef.current = dataUrlRef.current;
          // eslint-disable-next-line no-console
          console.warn("[watches-grid] engine download error detected after reload — rebuilding grid", dataUrlRef.current);
          rebuildGrid();
        }
      }, 300);
    };
    try {
      // ReloadBody's completion callback is verified working on this engine
      // build; the timeout is a safety net for a failed download (callback
      // may never fire) so the queue can't wedge shut.
      grid.ReloadBody(finish);
      setTimeout(finish, 8000);
    } catch (e) {
      reloadingRef.current = false;
      // Engine refused an in-place reload — fall back to one full navigation.
      window.location.assign(`/watches-list${buildPageQs(q) ? `?${buildPageQs(q)}` : ""}`);
    }
  };

  // In-place navigation: update the address bar (so refresh / copy-link / back
  // still work) and let the query-state effect below re-fetch the grid.
  const navigate = (next) => {
    const nextQs = buildPageQs(next);
    if (nextQs === buildPageQs(queryRef.current)) return;
    // Each user navigation earns a fresh auto-rebuild chance. The once-guard
    // exists to stop a rebuild LOOP within one navigation — without this
    // reset, the first recovery for a URL (and "clear" is always the same
    // URL) permanently disabled recovery for every later attempt.
    rebuiltForUrlRef.current = null;
    window.history.pushState(null, "", `/watches-list${nextQs ? `?${nextQs}` : ""}`);
    setQuery(next);
  };
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Dispose the engine instance this mount owns — on unmount AND right before
  // a rebuild replaces it. Dispose() unregisters the grid's id, which
  // otherwise collides on the next build ("Grid with id 'Json' already exists
  // or collides with some internal name" — verified: dispose-before-rebuild
  // never warns), and frees the dead instance's listeners. Cleanup timing on a
  // rebuild: the new tag is committed but not yet built, so findLiveGrid still
  // resolves the OLD instance here. A disposed grid stops passing
  // findLiveGrid's usable() checks, so it can never be double-disposed or
  // targeted again.
  useEffect(() => {
    return () => {
      const g = findLiveGrid();
      if (g && typeof g.Dispose === "function") {
        try {
          g.Dispose();
        } catch (e) {
          /* best effort — e.g. disposing mid-download */
        }
      }
    };
  }, [gridEpoch]);

  // Keep query state in sync with the address bar for navigations we didn't
  // initiate: browser back/forward (popstate) AND router pushes while this
  // page is already mounted — e.g. the sidebar "Watch Grid" button or the
  // sidebar/header watch search, which SPA-navigate to /watches-list?… without
  // remounting (the route key is pathname-only). Both land here and the query
  // effect reloads the grid in place. If the URL produces the same data URL,
  // the effect no-ops (string-compared dependency).
  useEffect(() => {
    const sync = () => {
      if (!window.location.pathname.endsWith("/watches-list")) return;
      // A back/forward or router navigation is a fresh user action — re-arm
      // the one-rebuild-per-navigation recovery guard (see navigate()).
      rebuiltForUrlRef.current = null;
      const q = readUrlQuery();
      setQuery(q);
      setSearchInput(q.search);
    };
    window.addEventListener("popstate", sync);
    const unlisten =
      Navigation.history && typeof Navigation.history.listen === "function"
        ? Navigation.history.listen(sync)
        : null;
    return () => {
      window.removeEventListener("popstate", sync);
      if (unlisten) unlisten();
    };
  }, []);

  // React to query/site changes (including the initial one): pre-check the row
  // count when the SEARCH changed (TreeGrid shows cryptic errors like "Failed
  // downloading page 0 from Page" when a dataset is empty, so an empty result
  // shows our message instead of touching the grid), then refresh the grid in
  // place. Sort/page/size changes can't alter the count, so they skip the
  // pre-check and reload immediately.
  const countedKeyRef = useRef(null);
  useEffect(() => {
    if (!token) return;
    if (statusRef.current === "failed") return;
    const countKey = `${effectiveSite}|${queryRef.current.search}`;
    if (countedKeyRef.current === countKey) {
      if (statusRef.current === "ready") reloadGrid();
      return;
    }
    let cancelled = false;
    const countParams = new URLSearchParams();
    countParams.set("site", String(effectiveSite));
    if (queryRef.current.search) countParams.set("search", queryRef.current.search);
    countParams.set("Num", "1");
    fetch(`${REACT_APP_API_URL}/watches-grid?${countParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        countedKeyRef.current = countKey;
        const count = data?.IO?.Count ?? 0;
        setTotal(count);
        if (count === 0) {
          setGridStatus("empty");
          return;
        }
        setGridStatus("ready");
        reloadGrid();
      })
      .catch(() => {
        // Count check failed — let TreeGrid try anyway.
        if (cancelled) return;
        setGridStatus("ready");
        reloadGrid();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dataUrl, gridBuilt, effectiveSite]);

  // GridE.js only auto-creates grids from <treegrid> tags during its initial
  // page-load scan. When React re-mounts this page later in the same document
  // (browser back/forward from edit-watch, or any SPA navigation back here),
  // the freshly rendered tag is never upgraded and the grid stays blank.
  // Mirror the vendor's own GridED.js loader: an unprocessed (childless)
  // <treegrid> tag in the DOM means StartTreeGrid() must be called to rescan.
  // GridE.js is loaded as a plain script (~1MB): on a slow connection every
  // React render/effect can happen while StartTreeGrid is still undefined, so
  // the poll RETRIES the call each tick until GridE.js arrives — a one-shot
  // call at effect time is a race that loses on prod. calledStart limits us to
  // one successful call per mount (TreeGrid consumes the tag while building;
  // repeated calls could double-create).
  // Fallback: if the grid still hasn't built after 8s, force ONE full reload
  // (sessionStorage marker prevents a loop); if it already reloaded once,
  // show the error state instead of spinning forever.
  useEffect(() => {
    if (!token || gridStatus === "empty" || gridStatus === "failed") return;
    const wrap = gridWrapRef.current;
    if (!wrap) return;
    const findTag = () => wrap.querySelector("treegrid");
    const isBuilt = () => {
      const tag = findTag();
      // built = tag replaced by TreeGrid's own markup, or (defensively) filled
      return (!tag && wrap.childNodes.length > 0) || (tag && tag.childNodes.length > 0);
    };
    let calledStart = false;
    const tryStart = () => {
      if (calledStart || typeof window.StartTreeGrid !== "function") return;
      const tag = findTag();
      if (tag && tag.childNodes.length === 0) {
        calledStart = true;
        window.StartTreeGrid();
      }
    };
    tryStart();
    const started = Date.now();
    const iv = setInterval(() => {
      if (isBuilt()) {
        clearInterval(iv);
        sessionStorage.removeItem("__tgForcedReload");
        setGridBuilt(true);
        // A build can complete WITH a failed data download painted inside it
        // (e.g. a transient backend hiccup at boot). Give it a beat, then
        // auto-rebuild once for this URL.
        setTimeout(() => {
          if (gridHasError() && rebuiltForUrlRef.current !== dataUrlRef.current) {
            rebuiltForUrlRef.current = dataUrlRef.current;
            // eslint-disable-next-line no-console
            console.warn("[watches-grid] engine download error detected after build — rebuilding grid", dataUrlRef.current);
            rebuildGrid();
          }
        }, 600);
        return;
      }
      tryStart();
      if (Date.now() - started > 8000) {
        clearInterval(iv);
        if (!sessionStorage.getItem("__tgForcedReload")) {
          sessionStorage.setItem("__tgForcedReload", "1");
          window.location.reload();
        } else {
          setGridStatus("failed");
        }
      }
    }, 150);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, gridStatus, gridEpoch]);

  useEffect(() => {
    window.__editWatchNav = (itemId) => {
      // Insert overlay synchronously via direct DOM — bypasses React scheduling
      // so the browser paints it before TreeGrid gets another frame to repaint.
      const overlay = document.createElement('div');
      overlay.id = '__watch-nav-overlay';
      overlay.style.cssText = 'position:fixed;inset:0;background:#fff;z-index:2147483647';
      document.body.appendChild(overlay);
      Navigation.go(`/edit-watch/${itemId}`);
    };
    // Same as __editWatchNav but appends #images so the edit screen scrolls
    // straight to the Images section on load (handled in AddWatchStepper).
    window.__editWatchImages = (itemId) => {
      const overlay = document.createElement('div');
      overlay.id = '__watch-nav-overlay';
      overlay.style.cssText = 'position:fixed;inset:0;background:#fff;z-index:2147483647';
      document.body.appendChild(overlay);
      Navigation.go(`/edit-watch/${itemId}#images`);
    };
    window.__viewItemOnSite = (row) => {
      if (row.stub) {
        const siteUrl = process.env.REACT_APP_COMPANY_SITE_URL || 'https://prestigetime7.bitbean.dev';
        window.open(`${siteUrl}/${row.stub}.html`, '_blank');
      } else {
        window.alert(`No URL stub found for item ${row.item_id}, so it has no front-facing page yet.`);
      }
    };
    // Set a date column to today (local midnight) from the grid's OnClickCell
    // handler on the thin td_added / td_reduced icon cells. SetValue with the
    // change flag marks the row dirty so it uploads through the normal grid
    // save (which also logs to watch history).
    window.__setToday = (grid, row, col) => {
      // Use UTC midnight of the LOCAL date — this matches how TreeGrid stores a
      // date picked from its calendar (a UTC-midnight epoch), so the date the
      // backend parses out is today's local date regardless of the viewer's
      // timezone. (Local-midnight would format to the previous day in UTC+ zones.)
      const now = new Date();
      const utcMidnight = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      try {
        grid.SetValue(row, col, utcMidnight, 1);
      } catch (e) {
        /* grid API shape guard — never throw out of a TreeGrid action */
      }
    };
    // Header sort click (from the grid's OnClickSort action). Sorting is
    // server-side across the whole dataset, so it navigates — but IN PLACE:
    // the grid body reloads via ReloadBody while the rest of the page stays
    // put. Clicking the active column flips its direction; a new column starts
    // ascending. Sorting resets to page 0.
    window.__tgSort = (col) => {
      const name = typeof col === 'string' ? col : (col && (col.Name || col.name));
      if (!name) return;
      const q = queryRef.current;
      const dir = q.sort === name && q.dir !== 'desc' ? 'desc' : 'asc';
      navigateRef.current({ ...q, sort: name, dir, page: 0 });
    };

    // Copy the model number when its copy icon is clicked. Runs in the CAPTURE
    // phase so we can stop the event before TreeGrid's OnClickCell navigates to
    // the edit screen.
    const copyText = (text) => {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
      }
      // Fallback for non-secure contexts where the async Clipboard API is blocked.
      return new Promise((resolve, reject) => {
        try {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand("copy");
          ta.remove();
          resolve();
        } catch (err) {
          reject(err);
        }
      });
    };
    const CHECK_SVG =
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
    const onGridCopyClick = (e) => {
      const icon = e.target.closest && e.target.closest(".grid-copy-icon");
      if (!icon) return;
      e.preventDefault();
      e.stopPropagation();
      const link = icon.parentElement?.querySelector(".grid-watch-link");
      const text = (link ? link.textContent : "").trim();
      if (!text) return;
      copyText(text)
        .then(() => {
          // Briefly swap the copy glyph for a green checkmark. Save the original
          // markup once so rapid re-clicks don't stash the checkmark itself.
          if (icon.__copyTimer) clearTimeout(icon.__copyTimer);
          else icon.__origHtml = icon.innerHTML;
          icon.innerHTML = CHECK_SVG;
          icon.classList.add("copied");
          icon.__copyTimer = setTimeout(() => {
            icon.innerHTML = icon.__origHtml;
            icon.classList.remove("copied");
            icon.__copyTimer = null;
          }, 1000);
        })
        .catch(() => {});
    };
    document.addEventListener("click", onGridCopyClick, true);

    // "Set to today" left-side icon on the two date cells. The icon is drawn by
    // CSS (::before on .grid-today), so clicks land on the cell itself — we only
    // treat a click in the left ~20px icon zone as "set today"; clicks further
    // right fall through to TreeGrid's normal edit/calendar. Capture phase +
    // stopPropagation keeps the edit from opening when the icon IS clicked.
    const onGridTodayClick = (e) => {
      const cell = e.target.closest && e.target.closest(".grid-today");
      if (!cell) return;
      const rect = cell.getBoundingClientRect();
      if (e.clientX - rect.left > 20) return; // clicked the date, not the icon
      e.preventDefault();
      e.stopPropagation();
      const grid = findLiveGrid();
      if (!grid) return;
      const col = cell.classList.contains("grid-today-added")
        ? "date_added"
        : cell.classList.contains("grid-today-reduced")
        ? "date_reduced"
        : null;
      if (!col) return;
      // Resolve the TreeGrid Row: the data <tr> carries it in its onmousemove
      // (TGGrids[0].Rows["<id>"]); fall back to the hovered row the grid tracks.
      let row = grid.ARow;
      const tr = cell.closest("tr");
      const mm = tr && tr.getAttribute("onmousemove");
      const m = mm && mm.match(/Rows\["([^"]+)"\]/);
      if (m && grid.Rows) row = grid.Rows[m[1]] || row;
      if (!row) return;
      window.__setToday(grid, row, col);
    };
    document.addEventListener("click", onGridTodayClick, true);

    const style = document.createElement("style");
    style.id = "grid-watch-link-style";
    style.textContent = `
      .grid-watch-link { cursor: pointer !important; color: #1976d2 !important; text-decoration: underline; }
      .grid-watch-link:hover { color: #1565c0 !important; }
      .grid-item-link { cursor: pointer !important; color: #388e3c !important; text-decoration: underline; }
      .grid-item-link:hover { color: #1b5e20 !important; }
      .grid-copy-icon { cursor: pointer; float: right; margin-right: 4px; color: #9aa0a6; display: inline-flex; vertical-align: middle; }
      .grid-copy-icon:hover { color: #1976d2; }
      .grid-copy-icon.copied { color: #2e7d32; }
      .grid-today { position: relative; padding-left: 20px !important; }
      .grid-today::before {
        content: "";
        position: absolute; left: 2px; top: 50%; width: 15px; height: 15px;
        transform: translateY(-50%);
        cursor: pointer; opacity: 0.55;
        background: no-repeat center / 15px 15px
          url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='%235a6573' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2'/%3E%3Cline x1='16' y1='2' x2='16' y2='6'/%3E%3Cline x1='8' y1='2' x2='8' y2='6'/%3E%3Cline x1='3' y1='10' x2='21' y2='10'/%3E%3Cpolyline points='9 16 11 18 15 14'/%3E%3C/svg%3E");
      }
      .grid-today:hover::before { opacity: 1; }
      /* Rows flagged "Marked For Retirement" (backend sets row Class:grid-retired,
         which TreeGrid stamps on every cell of the row). Pink matches the
         edit-screen retirement badge. */
      td.grid-retired { background-color: #fce4ec !important; }
      .grid-images-cell { cursor: pointer; }
      .grid-images-icon { color: #9aa0a6; display: inline-flex; vertical-align: middle; }
      .grid-images-cell:hover .grid-images-icon { color: #1976d2; }
      /* Thumbnail column (hidden by default, added via the columns menu). With
         Cfg VarHeight=1 the engine sets each row's height INLINE from what it
         MEASURES at render (there is no .TWRowHeight element in this mode).
         The cell must therefore have its final height BEFORE the image loads:
         an <img> arrives asynchronously, so rows measured before it landed
         stayed short and the last row (whose image is usually the straggler,
         loading after the final re-layout) rendered half-chopped. A fixed cell
         height makes the measurement deterministic on first render — verified:
         uniform 47px rows and a scroll canvas of exactly rows×47. Rows without
         the column stay compact (no cell → nothing to size). */
      .grid-thumb-cell { height: 46px !important; line-height: 46px !important; }
      .grid-thumb { max-height: 44px; max-width: 54px; object-fit: contain; vertical-align: middle; }
      .tg-thumbs-on .grid-thumb-cell,
      .tg-thumbs-on .grid-thumb-cell .TWSolidRow { overflow: visible !important; }
      /* While the grid BOOTS (html.tg-boot, toggled until gridBuilt): show one
         clean cover and nothing else. The building grid is visibility:hidden,
         but TreeGrid's own progress popups ("Updating tree…", "Rendering…")
         set visibility:visible INLINE, which pierces a hidden ancestor — so
         they must be display:none'd outright. Scoped to html.tg-boot so the
         normal save/feedback messages still show once the grid is live. */
      html.tg-boot .TWMessage,
      html.tg-boot .TWPageMessage,
      html.tg-boot .TWMessageShadow,
      html.tg-boot .TWMessageButtons { display: none !important; }
      html.tg-boot .tg-grid-wrap { visibility: hidden; }
      /* TreeGrid's developer debug strip (attached to BODY, outlives the grid
         that raised a message). Hidden — NOT removed: the engine keeps a live
         reference and crashes writing to a deleted element. */
      [class^="GridDebug"] { display: none !important; }
    `;
    document.head.appendChild(style);
    return () => {
      delete window.__editWatchNav;
      delete window.__editWatchImages;
      delete window.__viewItemOnSite;
      delete window.__tgSort;
      delete window.__setToday;
      document.removeEventListener("click", onGridCopyClick, true);
      document.removeEventListener("click", onGridTodayClick, true);
      document.getElementById("grid-watch-link-style")?.remove();
    };
  }, []);

  // Grow the data rows only while the thumbnail column is present. TreeGrid has
  // no reliable public hook for column show/hide (engine is compressed), so we
  // watch the grid DOM: when its cells (.grid-thumb-cell) exist the column is
  // visible → add .tg-thumbs-on (taller rows via CSS); when the user removes it
  // the cells vanish → rows snap back to the compact default. Also hide broken
  // thumbnails (many items lack a deep-path image); the img 'error' event does
  // not bubble, so it's caught in the capture phase.
  useEffect(() => {
    const wrap = gridWrapRef.current;
    if (!wrap) return;
    // Initialized from the wrap's real class state (not null) so the first
    // sync() on a fresh/rebuilt wrap with thumbs off is a NO-OP — the old
    // null-init fired a gratuitous resize nudge on every mount/rebuild, and
    // resize events wake OLD grid instances' global handlers.
    let thumbsOn = wrap.classList.contains("tg-thumbs-on");
    // The taller-row CSS (.tg-thumbs-on) is applied AFTER the grid has built and
    // measured its rows, so its scroll canvas is still sized to the default row
    // height and the extra height accumulates → the last row is clipped. The engine
    // has no public "recompute" hook, but it re-lays-out (re-reading current row
    // heights, honoring Cfg VarHeight=1) on a window resize — so nudge it once the
    // class is on. Only firing on the toggle edge / finite timers avoids an observer
    // loop (the resize mutates the grid, which would otherwise re-enter sync).
    const sync = () => {
      const on = !!wrap.querySelector(".grid-thumb-cell");
      if (on !== thumbsOn) { thumbsOn = on; wrap.classList.toggle("tg-thumbs-on", on); nudge(); }
    };
    sync();
    // The observer catches later column add/remove and normal (multi-row)
    // renders. But a single search result renders once and then the grid goes
    // completely static — if the cell's class lands a beat after our check,
    // there's no further mutation to re-trigger it and the lone row stays
    // compact. A few delayed re-checks after build cover that static case; each
    // also re-nudges the layout so a late row render gets a correct canvas.
    const timers = [80, 250, 600, 1200].map((t) => setTimeout(() => { sync(); if (thumbsOn) nudge(); }, t));
    const mo = new MutationObserver(sync);
    mo.observe(wrap, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
    const onImgError = (e) => {
      const t = e.target;
      if (t && t.tagName === "IMG" && t.classList.contains("grid-thumb")) {
        t.style.visibility = "hidden";
      }
    };
    wrap.addEventListener("error", onImgError, true);
    return () => {
      timers.forEach(clearTimeout);
      mo.disconnect();
      wrap.removeEventListener("error", onImgError, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, gridBuilt, gridEpoch]);

  // Live search: refresh after a short pause once 3+ chars are typed. Now that
  // the refresh is in-place (no page reload) the debounce can be snappy.
  // Skips when the input already matches the active search. A new search drops
  // the sort (so the backend's relevance ranking orders the results) and
  // resets to page 0, keeping the chosen page size.
  // An EMPTIED input navigates immediately (no debounce) — identical to the ✕
  // clear button, so backspacing everything and clicking ✕ behave the same.
  useEffect(() => {
    const term = searchInput.trim();
    if (term === query.search) return;
    if (term.length === 0 && query.search) {
      navigateRef.current({ ...queryRef.current, search: "", sort: "", dir: "asc", page: 0 });
      return;
    }
    if (term.length < 3) return;
    const timer = setTimeout(() => {
      navigateRef.current({ ...queryRef.current, search: term, sort: "", dir: "asc", page: 0 });
    }, 600);
    return () => clearTimeout(timer);
  }, [searchInput, query.search]);

  const handleSearch = () => {
    const term = searchInput.trim();
    navigate({ ...query, search: term, sort: "", dir: "asc", page: 0 });
  };
  const handleClear = () => {
    setSearchInput("");
    navigate({ ...query, search: "", sort: "", dir: "asc", page: 0 });
  };
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  const goToPage = (p) => navigate({ ...query, page: Math.max(0, p) });
  // Changing page size resets to page 0 (a deep page index rarely maps cleanly
  // to the new size, and staying put would confuse the "X–Y of Z" label).
  const changePageSize = (size) => navigate({ ...query, size, page: 0 });

  const startPos = query.page * query.size;
  const hasPrev = query.page > 0;
  const hasNext = total != null && startPos + query.size < total;
  // Show the pager bar whenever the grid is visible with rows — the size
  // dropdown stays useful even below one page (prev/next simply disable).
  // Gated on gridBuilt so it doesn't pop in under the boot cover and then
  // shift when the grid reveals.
  const showPager = gridBuilt && gridStatus === 'ready' && total != null && total > 0;

  const emptyMessage = query.search
    ? `No watches found for "${query.search}".`
    : 'No products found — please add a new watch!';

  // One local cover over the grid area only — the sidebar and the rest of the
  // page stay visible and interactive. It shows the first-load spinner until
  // TreeGrid has built, and doubles as the empty-result / failed surface.
  // In-place refreshes never bring it back: the old rows stay on screen until
  // ReloadBody swaps them.
  const coverActive = !gridBuilt || gridStatus === 'empty' || gridStatus === 'failed';

  // Boot phase = grid still building. html.tg-boot hides the building grid
  // AND TreeGrid's progress popups (see the style block above), so the only
  // thing visible in the grid area is the single cover — no "Updating tree…"/
  // "Rendering…" parade, no content shifting underneath. Cleared the moment
  // the grid is built (one clean swap), restoring normal message behavior.
  useEffect(() => {
    document.documentElement.classList.toggle('tg-boot', !gridBuilt);
    return () => document.documentElement.classList.remove('tg-boot');
  }, [gridBuilt]);

  return (
    <>
      <Box style={{ width: '100%', maxWidth: 1640, margin: '0 auto', paddingBottom: 4 }}>
        {token ? (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Paper variant="outlined" style={{ display: 'flex', alignItems: 'center', padding: '2px 8px' }}>
              <InputBase
                placeholder="Search brand, series, model or ID…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{ width: 260 }}
              />
              {searchInput && (
                <IconButton size="small" onClick={handleClear}><ClearIcon fontSize="small" /></IconButton>
              )}
              <IconButton size="small" onClick={handleSearch}><SearchIcon fontSize="small" /></IconButton>
            </Paper>
          </Box>
        ) : (
          <Typography>Loading...</Typography>
        )}
      </Box>
      {token && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: 1640,
            margin: '0 auto',
            // Reserve roughly the grid's final height while it builds so the
            // page doesn't reflow when it appears (grid body is capped at
            // MaxVScroll 800 + toolbar/header/filter rows).
            minHeight: coverActive ? (gridBuilt ? 420 : 880) : undefined,
          }}
        >
          {coverActive && (
            <Box
              style={{
                position: 'absolute',
                inset: 0,
                background: '#fff',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                // Pinned at a fixed offset from the top (not centered): the
                // container can still grow while the hidden grid builds, and a
                // centered spinner would visibly drift down with it.
                justifyContent: 'flex-start',
                paddingTop: 170,
                gap: 14,
                textAlign: 'center',
              }}
            >
              {gridStatus === 'empty' ? (
                <Typography variant="h6" style={{ color: '#555' }}>
                  {emptyMessage}
                </Typography>
              ) : gridStatus === 'failed' ? (
                <>
                  <Typography variant="h6" style={{ color: '#b00020' }}>
                    The watch grid failed to load.
                  </Typography>
                  <Typography
                    style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => { sessionStorage.removeItem('__tgForcedReload'); window.location.reload(); }}
                  >
                    Try again
                  </Typography>
                </>
              ) : (
                <>
                  <style>{'@keyframes __wpspin{to{transform:rotate(360deg)}}'}</style>
                  <div style={{ boxSizing: 'border-box', width: 34, height: 34, border: '3px solid #ddd', borderTopColor: '#1976d2', borderRadius: '50%', animation: '__wpspin .8s linear infinite' }} />
                  <div style={{ color: '#666', fontFamily: 'Roboto,Arial,sans-serif' }}>Loading watches…</div>
                </>
              )}
            </Box>
          )}
          <div key={gridEpoch} ref={gridWrapRef} className="tg-grid-wrap" style={{ width: '100%' }}>
            {/* upload_Header_SiteId: TreeGrid drops the ?site= query on the upload POST,
                so the current site also rides a header; updateGrid reads it. Without it,
                saves default to site 0 and write PT rows while editing in the TWS view. */}
            <treegrid
              is="treegrid"
              style={{ display: "block", width: "100%" }}
              layout_url={initialTagRef.current.layoutUrl}
              layout_Header_Authorization={`Bearer ${token}`}
              data_url={initialTagRef.current.dataUrl}
              data_Header_Authorization={`Bearer ${token}`}
              upload_url={`${REACT_APP_API_URL}/update-grid?site=${effectiveSite}`}
              upload_Header_Authorization={`Bearer ${token}`}
              upload_Header_SiteId={String(effectiveSite)}
              upload_Format="JSON"
            ></treegrid>
          </div>
        </div>
      )}
      {token && showPager && (
        <Box
          style={{
            width: '100%',
            maxWidth: 1640,
            margin: '2px auto 0',
            paddingRight: 320,
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Typography style={{ fontSize: 12, color: '#555' }}>Rows:</Typography>
          <select
            value={query.size}
            onChange={(e) => changePageSize(parseInt(e.target.value, 10))}
            title="Rows per page"
            style={{
              fontSize: 12,
              color: '#555',
              padding: '2px 4px',
              border: '1px solid #ccc',
              borderRadius: 4,
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <Typography style={{ fontSize: 12, color: '#555', marginLeft: 6 }}>
            {startPos + 1}–{Math.min(startPos + query.size, total)} of {total}
          </Typography>
          <IconButton size="small" onClick={() => goToPage(query.page - 1)} disabled={!hasPrev} title="Previous page">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => goToPage(query.page + 1)} disabled={!hasNext} title="Next page">
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </>
  );
}

export const WatchesPage = React.memo(_WatchesPage);